import { formatKickoff, liveLabel } from '../../shared/clock'
import type { Side } from '../../shared/espn'
import type { StateGame } from '../api'
import { TeamLogo } from '../ui'

/*
 * One game, and who took which side. Built from design_screenshots/game-sheet.png.
 *
 * ONE SHAPE NOW: two team cards, and under each the people who took that side.
 *
 * It used to have a second shape - before kickoff, a single number, "13 PICKS
 * IN", and nothing else - because the server stripped every pick on a game
 * that had not started, and splitting even a total per side would have leaked
 * what it refused to send. Picks became public the moment they are made on
 * 2026-09-17, so there is nothing left to withhold and no reason for the sheet
 * to have a before and an after.
 *
 * What changes before kickoff is only what exists: no scores, and a kickoff
 * time instead.
 *
 * Gold marks what YOU did. It does not mark who is winning - the bigger score
 * already says that, and a gold card that meant both would mean neither.
 */
export function GameSheet({
  game,
  index,
  players,
  picks,
  meId,
  tz,
  onBack,
}: {
  game: StateGame
  /** 1-based position on the board, for the "GAME 3" title. */
  index: number
  players: readonly { id: string; name: string }[]
  /** Every pick on this game. Nothing is withheld any more. */
  picks: readonly { gameId: string; playerId: string; side: Side }[]
  meId: string | null
  tz: string
  onBack: () => void
}) {
  const mine = picks.filter((p) => p.gameId === game.id)
  const myPick = meId === null ? null : (mine.find((p) => p.playerId === meId)?.side ?? null)

  /*
   * Roster order, not alphabetical and not "me first". The two columns are read
   * side by side, and a stable order across every game on the board is what lets
   * you find a person in the same place each time.
   */
  const took = (side: Side) => {
    const on = new Set(mine.filter((p) => p.side === side).map((p) => p.playerId))
    return players.filter((pl) => on.has(pl.id))
  }
  const away = took('away')
  const home = took('home')

  const live = game.started && game.result?.final !== true
  const final = game.result?.final === true

  /*
   * The line, derived from favSide and spread rather than from ESPN's own
   * `detail` string. Those are the numbers the board was frozen with and the
   * ones the underdog bonus settles against; the two can disagree, and when
   * they do the frozen pair is the one everybody picked against.
   */
  const favName = game.favSide === 'home' ? game.home.name : game.away.name
  const line = game.spread === null || game.favSide === null ? null : `${favName} −${game.spread}`

  /*
   * Built as parts and joined, so a game with no television slot and no posted
   * line does not render a row of orphaned separators.
   */
  const subline = [
    game.networks[0]?.toUpperCase() ?? null,
    line?.toUpperCase() ?? null,
    game.started ? `KICKED ${formatKickoff(game.kickoffAt, tz).toUpperCase()}` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="pb-5">
      <header className="flex items-center justify-between gap-3 px-3 pt-4 pb-1">
        {/*
         * The arrow AND the title are one control.
         *
         * The design draws a 20px arrow, and nothing tappable in this app is
         * under 44px in its smallest dimension - the youngest player is eight.
         * A 44px box around a 20px arrow would shove the title a quarter-inch
         * off the rail every other screen aligns to. Making the whole cluster
         * the target keeps the drawn spacing and makes the target far larger
         * than the floor, which is the direction to err in.
         */}
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to the board"
          className="slate-tap -my-1 flex items-center gap-2 text-left"
        >
          <span aria-hidden className="text-[19px] leading-none text-faint">&larr;</span>
          <span className="text-[24px] leading-none font-extrabold tracking-[0.03em] uppercase">
            Game {index}
          </span>
        </button>

        {/*
         * Mint is TIME and only time, so a game in progress gets it and a
         * finished one does not.
         *
         * The design draws a period and a game clock here, and they arrive on
         * the RESULT, not on the game: the board is frozen at publish and a
         * ticking value on it would undo that, so the clock rides with the
         * score the refresh fetched it alongside. Both are nullable and both
         * are null most of the time, which `liveLabel` handles by degrading to
         * the bare LIVE this badge showed before it had anything else to say.
         *
         * FINAL is shown rather than left blank because an empty slot would
         * make "over" indistinguishable from "not loaded".
         */}
        {live && (
          <span className="slate-num shrink-0 text-[13px] font-bold tracking-[0.1em] text-live-deep">
            &#9679; {liveLabel(game.result?.period ?? null, game.result?.clock ?? null)}
          </span>
        )}
        {final && (
          <span className="slate-num shrink-0 text-[13px] font-bold tracking-[0.1em] text-mute">
            FINAL
          </span>
        )}
      </header>

      {subline && (
        <p className="slate-num mt-1.5 mb-0 px-3 text-[10.5px] leading-[1.5] tracking-[0.06em] text-mute uppercase">
          {subline}
        </p>
      )}

      {/*
       * The team cards get their own grid row so the two stay the same
       * height no matter how the names below them stack up.
       */}
      <div className="mt-3 grid grid-cols-2 gap-2 px-3">
        <TeamCard
          name={game.away.name}
          logo={game.away.logo}
          score={game.result?.awayScore ?? null}
          marked={myPick === 'away'}
        />
        <TeamCard
          name={game.home.name}
          logo={game.home.logo}
          score={game.result?.homeScore ?? null}
          marked={myPick === 'home'}
        />
      </div>

      {/* Two independent lists. A short side simply ends. */}
      <div className="mt-2 grid grid-cols-2 items-start gap-2 px-3">
        <Column people={away} meId={meId} />
        <Column people={home} meId={meId} />
      </div>

      <div className="slate-card mx-3 mt-3 flex items-baseline justify-between gap-3 px-3 py-2.5">
        <span className="slate-num min-w-0 text-[11px] leading-[1.5] font-bold tracking-[0.06em] uppercase">
          {away.length} took {game.away.name} &middot; {home.length} took {game.home.name}
        </span>
        {/*
         * Slate, not gold. Your own row already carries the gold rule and
         * your card already carries the gold edge; a third gold on the same
         * screen stops meaning "you" and starts meaning "important".
         */}
        {myPick !== null && (
          <span className="slate-num shrink-0 text-right text-[11px] leading-[1.5] tracking-[0.06em] text-slate uppercase">
            You: {myPick === 'home' ? game.home.name : game.away.name}
          </span>
        )}
      </div>

      {/* Still to come: when, and how many are in. Both are facts the
          board already shows; neither is a secret now. */}
      {!game.started && (
        <div className="slate-card mx-3 mt-2 flex items-baseline justify-between gap-3 px-3 py-2.5">
          <span className="slate-num min-w-0 text-[11px] leading-[1.5] tracking-[0.06em] text-slate uppercase">
            {game.pickCount} {game.pickCount === 1 ? 'pick' : 'picks'} in &middot; open until kickoff
          </span>
          <span className="slate-num shrink-0 text-right text-[11px] leading-[1.5] tracking-[0.06em] text-slate uppercase">
            {formatKickoff(game.kickoffAt, tz)}
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * One side of the game: the team, its score, and whether YOU took it.
 *
 * `marked` is the pick, never the lead. Parchment and a gold edge for the side
 * you are on, a plain white card for the other, and the scores are left to say
 * which of the two is actually ahead.
 */
function TeamCard({
  name,
  logo,
  score,
  marked,
}: {
  name: string
  logo: string | null
  score: number | null
  marked: boolean
}) {
  return (
    <div
      className={[
        'slate-card min-w-0 px-2.5 py-3 text-center',
        marked ? 'bg-gold-wash' : '',
      ].join(' ')}
      /*
       * Border plus a 1px inset ring, because the design's edge is two device
       * pixels, and swapping only the border tint reads as a hairline next to
       * the parchment fill.
       */
      style={
        marked
          ? {
              borderColor: 'var(--color-gold-edge)',
              boxShadow: 'inset 0 0 0 1px var(--color-gold-edge)',
            }
          : undefined
      }
    >
      {/* Centred above the name: this card is the widest a crest ever gets
          to be, and the sheet is the one screen with room to spare. */}
      <span className="mb-1.5 flex justify-center">
        <TeamLogo src={logo} size={36} />
      </span>

      <span className="block text-[19px] leading-none font-bold tracking-[0.02em] uppercase">
        {name}
      </span>
      <span className="slate-num mt-2 block text-[24px] leading-none font-bold">
        {score ?? '—'}
      </span>
    </div>
  )
}

/** The people who took one side, in roster order. */
function Column({
  people,
  meId,
}: {
  people: readonly { id: string; name: string }[]
  meId: string | null
}) {
  return (
    <ul className="flex min-w-0 flex-col gap-1">
      {people.map((p) => (
        <li
          key={p.id}
          className="slate-card flex min-h-[36px] items-center px-2.5 py-1.5"
          /*
           * A full-height rule rather than a left border, so the row keeps the
           * same width and text inset as every other row in the column - a
           * 3px border would shift your own name 3px right of everyone else's.
           */
          style={p.id === meId ? { boxShadow: 'inset 3px 0 0 var(--color-gold)' } : undefined}
        >
          <span className="min-w-0 text-[17px] leading-none font-bold tracking-[0.02em] uppercase">
            {p.name}
          </span>
        </li>
      ))}
    </ul>
  )
}
